import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCommercialProjectRow } from "./commercialProjectAcf";
import { filterExpansionRowsForBase } from "./expansionGroupUtils";
import { isCvinExpansion } from "../projectGridColumns";
import { canAddCommercialWorkOrderChild } from "./commercialRoleAccess";
import { getViewerEmailFromStorage, hasEffectiveAdminPrivileges } from "./userMatrixStorage";
import { displayPhaseForRow } from "../projectPhaseDisplay";
import { isRevenueCommercialProjectType, serviceSpeedLabel } from "./commercialProjectSchema";
import { confirmAndArchiveDeleteProjects } from "./projectArchiveDelete";
import {
  compareExpansionWorkOrderDisplay,
  normalizeExpansionWorkOrderDisplay,
} from "./expansionWorkOrderFormat";
import "./parentWorkOrderPage.css";

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function connectionLabel(row) {
  const summary = row?.acf_groups_json?.project_summary || {};
  const conn = summary.connection_type;
  if (Array.isArray(conn) && conn.length) return conn.join(", ");
  const networking = row?.acf_groups_json?.networking_details || {};
  const netConn = networking.connection;
  if (Array.isArray(netConn) && netConn.length) return netConn.join(", ");
  return "—";
}

function speedLabel(row) {
  const summary = row?.acf_groups_json?.project_summary || {};
  const networking = row?.acf_groups_json?.networking_details || {};
  return serviceSpeedLabel(networking.service_speed ?? summary.service_speed) || "—";
}

function isParentShellRow(row, base) {
  const wo = String(row?.work_order ?? "").trim();
  return wo === String(base);
}

function isChildRow(row, base) {
  const wo = String(row?.work_order ?? "").trim();
  return wo.startsWith(`${base}.`);
}

export default function ParentWorkOrderPage({ apiUrl, getAuthHeaders }) {
  const { base } = useParams();
  const navigate = useNavigate();
  const viewerEmail = getViewerEmailFromStorage();
  const canAddChild = canAddCommercialWorkOrderChild(viewerEmail);
  const canDeleteGroup = hasEffectiveAdminPrivileges();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [expansionSegmentBase, setExpansionSegmentBase] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = apiUrl || getApiBase();
      const { data } = await axios.get(`${url}/projects`, { headers: getAuthHeaders() });
      const allProjects = Array.isArray(data) ? data : [];
      const woBase = String(base || "").trim();
      const expansionMatched = filterExpansionRowsForBase(allProjects.filter(isCvinExpansion), woBase);
      const hasExpansionSubsegment = expansionMatched.some((row) => {
        const wo = String(row.work_order || "").trim();
        return wo.includes(".");
      });
      if (hasExpansionSubsegment) {
        setExpansionSegmentBase(woBase);
        setRows([]);
        setError("");
        return;
      }
      setExpansionSegmentBase("");
      const all = allProjects.filter(isCommercialProjectRow);
      const matched = all.filter((row) => {
        const rowBase =
          row.work_order_base != null && String(row.work_order_base).trim() !== ""
            ? String(row.work_order_base).trim()
            : String(row.work_order ?? "").split(".")[0];
        return rowBase === woBase;
      });
      setRows(matched);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load work order group.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, base, getAuthHeaders]);

  useEffect(() => {
    void load();
  }, [load]);

  const parent = useMemo(
    () => rows.find((row) => isParentShellRow(row, base)) || null,
    [rows, base]
  );

  const children = useMemo(() => {
    const childRows = rows.filter((row) => isChildRow(row, base));
    return childRows.sort((a, b) =>
      compareExpansionWorkOrderDisplay(a.work_order, b.work_order)
    );
  }, [rows, base]);

  const summarySource = parent || children[0] || null;

  const metrics = useMemo(() => {
    const sourceRows = children;
    let totalMrc = 0;
    let totalNrc = 0;
    let mrcCount = 0;
    let nrcCount = 0;
    const phases = {};
    for (const row of sourceRows) {
      const mrc = row.mrc != null ? Number(row.mrc) : null;
      const nrc = row.non_recurring_charge != null ? Number(row.non_recurring_charge) : null;
      if (Number.isFinite(mrc)) {
        totalMrc += mrc;
        mrcCount += 1;
      }
      if (Number.isFinite(nrc)) {
        totalNrc += nrc;
        nrcCount += 1;
      }
      const phase = displayPhaseForRow(row).trim() || "Unknown";
      phases[phase] = (phases[phase] || 0) + 1;
    }
    return {
      locationCount: children.length,
      totalMrc: mrcCount ? totalMrc : null,
      totalNrc: nrcCount ? totalNrc : null,
      phases,
    };
  }, [children]);

  const onArchiveDeleteGroup = useCallback(async () => {
    const ids = rows.map((row) => row.id).filter((id) => id != null);
    if (!ids.length) return;
    const childCount = children.length;
    const label =
      childCount > 0
        ? `work order group ${base} (${ids.length} project${ids.length === 1 ? "" : "s"}, including ${childCount} sub-work order${childCount === 1 ? "" : "s"})`
        : `work order group ${base}`;
    setArchiveError("");
    setArchiving(true);
    try {
      const result = await confirmAndArchiveDeleteProjects(ids, { label });
      if (result.cancelled) return;
      navigate("/", { replace: true });
    } catch (err) {
      setArchiveError(
        err?.response?.data?.error || err.message || "Could not archive work order group."
      );
    } finally {
      setArchiving(false);
    }
  }, [base, children.length, navigate, rows]);

  if (loading) {
    return (
      <div className="parent-wo-page">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
        <p className="project-detail-muted">Loading work order {base}…</p>
      </div>
    );
  }

  if (expansionSegmentBase) {
    return <Navigate to={`/expansion/segment/${encodeURIComponent(expansionSegmentBase)}`} replace />;
  }

  if (error) {
    return (
      <div className="parent-wo-page">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
        <p className="project-detail-error">{error}</p>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="parent-wo-page">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
        <p className="project-detail-error">No projects found for work order {base}.</p>
      </div>
    );
  }

  const customer = summarySource?.customer || "—";
  const projectType = summarySource?.project_type || "—";
  const isRevenue = isRevenueCommercialProjectType(projectType);
  const term = isRevenue ? summarySource?.term_in_months : null;
  const salesRep = isRevenue ? summarySource?.sales_rep || "—" : null;
  const status = summarySource?.status || "—";

  return (
    <div className="parent-wo-page">
      <Link to="/" className="project-detail-back">
        ← Back to board
      </Link>

      <header className="parent-wo-header">
        <h1 className="parent-wo-title">
          Work order {base}
          {customer !== "—" ? ` — ${customer}` : ""}
        </h1>
        <p className="parent-wo-subtitle">
          Work order group · {metrics.locationCount} sub-work order
          {metrics.locationCount === 1 ? "" : "s"}
        </p>
        {canAddChild ? (
          <p style={{ margin: "12px 0 0" }}>
            <Link to={`/work-order/${base}/add`} className="add-project-submit" style={{ display: "inline-block", textDecoration: "none" }}>
              + Add sub-work order
            </Link>
          </p>
        ) : null}
        {canDeleteGroup ? (
          <div className="parent-wo-header-actions">
            <button
              type="button"
              className="parent-wo-delete-btn"
              disabled={archiving}
              onClick={() => void onArchiveDeleteGroup()}
            >
              {archiving ? "Archiving…" : "Delete work order group"}
            </button>
          </div>
        ) : null}
        {archiveError ? (
          <p className="project-detail-error" role="alert" style={{ marginTop: 12 }}>
            {archiveError}
          </p>
        ) : null}
      </header>

      <div className="parent-wo-kpis">
        <div className="parent-wo-kpi">
          <span className="parent-wo-kpi-label">Sub-work orders</span>
          <strong>{metrics.locationCount}</strong>
        </div>
        {isRevenue ? (
          <>
            <div className="parent-wo-kpi">
              <span className="parent-wo-kpi-label">Total MRC</span>
              <strong>{formatMoney(metrics.totalMrc)}</strong>
            </div>
            <div className="parent-wo-kpi">
              <span className="parent-wo-kpi-label">Total NRC</span>
              <strong>{formatMoney(metrics.totalNrc)}</strong>
            </div>
            <div className="parent-wo-kpi">
              <span className="parent-wo-kpi-label">Term</span>
              <strong>{term != null ? `${term} mo` : "—"}</strong>
            </div>
          </>
        ) : null}
        <div className="parent-wo-kpi">
          <span className="parent-wo-kpi-label">Type</span>
          <strong>{projectType}</strong>
        </div>
        <div className="parent-wo-kpi">
          <span className="parent-wo-kpi-label">Status</span>
          <strong>{status}</strong>
        </div>
      </div>

      <section className="parent-wo-section">
        <h2 className="parent-wo-section-title">Order details</h2>
        <dl className="parent-wo-details">
          <div>
            <dt>Customer</dt>
            <dd>{customer}</dd>
          </div>
          {isRevenue ? (
            <>
              <div>
                <dt>Sales rep</dt>
                <dd>{salesRep}</dd>
              </div>
              <div>
                <dt>Term</dt>
                <dd>{term != null ? `${term} months` : "—"}</dd>
              </div>
            </>
          ) : null}
        </dl>
      </section>

      {Object.keys(metrics.phases).length ? (
        <section className="parent-wo-section">
          <h2 className="parent-wo-section-title">Phase breakdown</h2>
          <div className="parent-wo-phase-tags">
            {Object.entries(metrics.phases)
              .sort((a, b) => b[1] - a[1])
              .map(([phase, count]) => (
                <span key={phase} className="parent-wo-phase-tag">
                  {phase}: {count}
                </span>
              ))}
          </div>
        </section>
      ) : null}

      <section className="parent-wo-section">
        <h2 className="parent-wo-section-title">Sub-work orders</h2>
        <div className="parent-wo-table-wrap">
          <table className="parent-wo-table">
            <thead>
              <tr>
                <th>Work order</th>
                <th>Project</th>
                <th>Location</th>
                {isRevenue ? (
                  <>
                    <th>Service types</th>
                    <th>Speed</th>
                    <th>MRC</th>
                    <th>NRC</th>
                  </>
                ) : null}
                <th>Phase</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {children.length ? (
                children.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/project/${row.id}`} className="grid-cell-link">
                        {normalizeExpansionWorkOrderDisplay(row.work_order) || "—"}
                      </Link>
                    </td>
                    <td>{row.name || "—"}</td>
                    <td>{row.service_location || "—"}</td>
                    {isRevenue ? (
                      <>
                        <td>{connectionLabel(row)}</td>
                        <td>{speedLabel(row)}</td>
                        <td>{formatMoney(row.mrc)}</td>
                        <td>{formatMoney(row.non_recurring_charge)}</td>
                      </>
                    ) : null}
                    <td>{displayPhaseForRow(row).trim() || "—"}</td>
                    <td>{row.status || "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isRevenue ? 9 : 5} className="project-detail-muted">
                    No sub-work orders yet.
                    {canAddChild ? (
                      <>
                        {" "}
                        <Link to={`/work-order/${base}/add`}>Add the first sub-work order</Link>
                      </>
                    ) : null}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function ParentWorkOrderGate(props) {
  return <ParentWorkOrderPage {...props} />;
}
